import React, { useState } from 'react';
import { AlternativeSummary } from '../types';
import JourneyRouteMap, { RouteStop } from '../../maps/JourneyRouteMap';
import { useViewportWidth } from '../../../hooks/useViewportWidth';
import RouteBadge from './RouteBadge';
import MainTag from './MainTag';
import SummaryChips from './SummaryChips';
import KebabMenu, { KebabItem } from './KebabMenu';
import { CAN_HOVER } from '../../../shared/canHover';
// CarbonScale is temporarily hidden (carbon intensity not surfaced for now);
// the component is kept for easy re-enable. import CarbonScale from './CarbonScale';

// One grouped, bordered unit so it reads as the selected route (design-handoff.md C.2
// step 5): FocusHeader + RouteMap + SummaryPanel (chips + carbon scale).
// On desktop (DetailSplitLayout) the trip pages, the owner's and the shared
// view, drop the chips strip and stack one of these per alternative instead
// (`row`, through AlternativesView), without the map: the map is the page's
// right-hand panel (RouteFocusMap).

export interface RouteFocusProps {
  alt: AlternativeSummary;
  onSetMain: (journeyId: string) => void;
  onSeeDetails: (journeyId: string) => void;
  settingMain?: boolean;
  /** Hovered (not selected) alternative: the map previews its route while the
   *  rest of the card keeps showing the selected one. */
  previewAlt?: AlternativeSummary | null;
  /** Planner's one-line "why this route" (alternativePlanService), when this
   *  alternative came from a plan. */
  reason?: string | null;
  /** False on read-only shared views: SummaryChips drops its "add prices"
   *  invite (a visitor can't price anything). */
  invitePricing?: boolean;
  /** Desktop list row: this card stands for one alternative in a stacked
   *  list (no map, no hover preview of other routes). */
  row?: RouteRowProps;
}

export interface RouteRowProps {
  selected: boolean;
  /** Like the chips: a click selects the card (the side map shows its route),
   *  a click on the selected card opens the route. */
  onSelect: (journeyId: string) => void;
  /** Hover preview on the side map: the id on mouse enter, null on leave. */
  onHoverChange?: (journeyId: string | null) => void;
  /** The chip's ⋮ menu (alternativeMenuItems), so its actions stay reachable. */
  menuItems: KebabItem[];
}

// Hovering another alternative's chip previews it: the card and the map show
// the hovered route until the mouse leaves.
const shownAlternative = (alt: AlternativeSummary, previewAlt?: AlternativeSummary | null): AlternativeSummary =>
  previewAlt && previewAlt.id !== alt.id ? previewAlt : alt;

// Shared outlined-green pill so the header actions read as one consistent set.
const pillButton: React.CSSProperties = {
  padding: '5px 12px',
  borderRadius: 999,
  border: '1.5px solid #157a55',
  background: 'transparent',
  color: '#157a55',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const FocusHeader: React.FC<RouteFocusProps> = ({ alt, onSetMain, onSeeDetails, settingMain, row }) => {
  const [titleHover, setTitleHover] = useState(false);
  // The standalone "See details" button is redundant with the clickable title,
  // so it's dropped on narrow (mobile) screens to keep the header on one row.
  const showSeeDetails = useViewportWidth() >= 480;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      <RouteBadge index={alt.routeIndex} />
      {/* The focused title is itself the primary way into the journey detail. */}
      <button
        onClick={() => onSeeDetails(alt.id)}
        onMouseEnter={() => setTitleHover(true)}
        onMouseLeave={() => setTitleHover(false)}
        title="Open journey details ↗"
        style={{
          flex: 1,
          textAlign: 'left',
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 18,
          fontWeight: 600,
          color: '#16241d',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textDecoration: titleHover ? 'underline' : 'none',
        }}
      >
        {alt.title}
      </button>
      {/* MAIN sits after the title (alongside the actions). */}
      {alt.isMain && <MainTag />}
      {/* Actions aligned with the title, top-right. In the desktop list only
          the selected card shows them (the ⋮ menu has both). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {(!row || row.selected) && !alt.isMain && (
          <button
            onClick={() => onSetMain(alt.id)}
            disabled={settingMain}
            style={{ ...pillButton, cursor: settingMain ? 'default' : 'pointer', opacity: settingMain ? 0.6 : 1 }}
          >
            ★ Set as main
          </button>
        )}
        {showSeeDetails && (!row || row.selected) && (
          <button onClick={() => onSeeDetails(alt.id)} style={pillButton}>
            See details ↗
          </button>
        )}
        {/* stopPropagation: the menu is portalled, but React still bubbles
            its clicks to the card, which would select/open the route. */}
        {row && (
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <KebabMenu ariaLabel="Alternative options" items={row.menuItems} />
          </span>
        )}
      </div>
    </div>
  );
};

export interface RouteFocusMapProps {
  /** The selected alternative. */
  alt: AlternativeSummary;
  /** Hovered (not selected) alternative, previewed instead while hovered. */
  previewAlt?: AlternativeSummary | null;
  onSeeDetails: (journeyId: string) => void;
  /** Fill the parent edge to edge (the desktop side panel) instead of the
   *  240px map inside the card. */
  fill?: boolean;
}

/** The focused route's map: inside the RouteFocus card on phones and tablets,
 *  in the right-hand panel on desktop (TripDetailPage). */
export const RouteFocusMap: React.FC<RouteFocusMapProps> = ({ alt, previewAlt, onSeeDetails, fill = false }) => {
  const shownAlt = shownAlternative(alt, previewAlt);
  const previewing = shownAlt.id !== alt.id;
  const mapStops: RouteStop[] = shownAlt.mapStops.map((s) => ({
    id: s.id,
    lat: s.lat ?? NaN, // JourneyRouteMap filters non-finite / (0,0)
    lng: s.lng ?? NaN,
    label: s.label,
    arrivalMode: s.arrivalMode,
  }));

  /* The whole map preview is a click target into the journey detail. The
     overlay button sits above the Mapbox canvas, so the preview is not
     pannable/zoomable here — full map interaction lives on the detail page.
     Exception: the map's legend toggle is stacked above this overlay
     (JourneyRouteMap's Legend zIndex), so it stays clickable. */
  return (
    <div
      style={fill
        ? { position: 'relative', height: '100%' }
        : { position: 'relative', borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}
    >
      <JourneyRouteMap stops={mapStops} height={240} fill={fill} />
      <button
        onClick={() => onSeeDetails(shownAlt.id)}
        aria-label="Open journey details"
        title="Open journey details ↗"
        style={{
          position: 'absolute',
          inset: 0,
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
        }}
      />
      {/* Whole-card swap cue: the title/badge already show the hovered route,
          so this only marks the state as temporary (reverts on mouse leave).
          Top-right: the map's legend occupies the top-left corner. */}
      {previewing && (
        <span
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(22,36,29,0.82)',
            color: '#ffffff',
            fontSize: 12,
            fontWeight: 600,
            pointerEvents: 'none',
          }}
        >
          Previewing
        </span>
      )}
    </div>
  );
};

const RouteFocus: React.FC<RouteFocusProps> = (props) => {
  const { alt, previewAlt, reason, row } = props;
  // Hovering another alternative's chip previews it on the WHOLE card — badge,
  // title, MAIN tag, chips AND map all switch to the hovered route (and animate
  // back on mouse leave), so the card never mixes two routes' facts. A list
  // row always shows its own route.
  const shownAlt = row ? alt : shownAlternative(alt, previewAlt);

  // List row: outlined like a chip when selected or hovered.
  const [hovered, setHovered] = useState(false);
  const hoverEnabled = !!row && CAN_HOVER;
  const outlined = !!row && (row.selected || hovered);
  // Clicks on the card's own buttons (title, actions, "Update route") do
  // their own thing; anywhere else selects, or opens the selected route.
  const onRowClick = row
    ? (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button')) return;
        if (row.selected) props.onSeeDetails(alt.id);
        else row.onSelect(alt.id);
      }
    : undefined;

  return (
    <section
      onClick={onRowClick}
      onMouseEnter={hoverEnabled ? () => { setHovered(true); row!.onHoverChange?.(alt.id); } : undefined}
      onMouseLeave={hoverEnabled ? () => { setHovered(false); row!.onHoverChange?.(null); } : undefined}
      aria-current={row?.selected || undefined}
      style={{
        border: `1.5px solid ${outlined ? '#157a55' : 'rgba(20,40,30,0.10)'}`,
        boxShadow: outlined ? '0 2px 10px rgba(21,122,85,0.14)' : 'none',
        borderRadius: 20,
        padding: 16,
        background: '#ffffff',
        cursor: row ? 'pointer' : undefined,
      }}
    >
      <FocusHeader {...props} alt={shownAlt} />
      {/* Why Jody picked this route (planner decision), when there is one. The
          parent passes the reason for the SHOWN alternative (preview included). */}
      {reason && (
        <p style={{ margin: '-4px 0 12px', fontSize: 13, color: '#6c7872', lineHeight: 1.4 }}>
          {reason}
        </p>
      )}
      {shownAlt.isStale && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 14,
            padding: '8px 12px',
            borderRadius: 12,
            background: '#fdf0d5',
            border: '1px solid rgba(138,90,0,0.20)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#8a5a00', fontSize: 13, fontWeight: 600, minWidth: 0 }}>
            <span aria-hidden>⚠</span>
            Based on older trip settings
          </span>
          <button
            onClick={() => props.onSeeDetails(shownAlt.id)}
            style={{
              flexShrink: 0,
              border: 'none',
              background: 'transparent',
              color: '#8a5a00',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              whiteSpace: 'nowrap',
            }}
          >
            Update route
          </button>
        </div>
      )}
      {!row && <RouteFocusMap alt={alt} previewAlt={previewAlt} onSeeDetails={props.onSeeDetails} />}
      {/* "See details" now lives in the header (top-right), so the chips fill
          the full width here. */}
      <SummaryChips summary={shownAlt} invitePricing={props.invitePricing} />
    </section>
  );
};

export default RouteFocus;
